import React, { FunctionComponent, useMemo } from "react";
import { ScheduleConfigVersionType } from "@iventis/domain-model/model/scheduleConfigVersionType";
import { DataField } from "@iventis/domain-model/model/dataField";
import { styled, formFieldVerticalGap, formGap, Header5, media, StyledFieldLabel } from "@iventis/styles";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations";

import { TextField } from "@mui/material";
import { DataFieldServicesContext, DataFieldsChips, IDataFieldServices } from "@iventis/datafield-editor";
import { getScheduleVersionDataFields } from "./edit-schedule-version-type.helpers";
import { ScheduleConfigDatafieldsAdditionalFormData } from "./schedule-config-datafields";

interface EditScheduleVersionTypeProps {
    title: string;
    scheduleVersionType: ScheduleConfigVersionType;
    dataFields: DataField[];
    onValueChange: (scheduleVersionType: ScheduleConfigVersionType) => void;
    allowNameEdit: boolean;
}

export const EditScheduleVersionType: FunctionComponent<EditScheduleVersionTypeProps> = ({ title, scheduleVersionType, dataFields, onValueChange, allowNameEdit }) => {
    const translate = useIventisTranslate();

    const scheduleVersionDataFields = useMemo(() => getScheduleVersionDataFields(dataFields, scheduleVersionType.dataFields), [dataFields, scheduleVersionType.dataFields]);

    const valueChange = <PropertyType extends keyof ScheduleConfigVersionType>(property: PropertyType, value: ScheduleConfigVersionType[PropertyType]) => {
        onValueChange({ ...scheduleVersionType, [property]: value });
    };
    const dataFieldServices: IDataFieldServices = useMemo(
        () => ({
            dataFieldsService: {
                postDataField: async () => null,
                putDataField: async () => null,
                deleteDataField: async () => null,
                getDataFieldsForResource: async () => [],
                isDataFieldBeingUsed: async () => null,
            },
            dataFieldListItemsService: {
                postDataFieldListItem: async () => null,
                putDataFieldListItem: async () => null,
                deleteDataFieldListItem: async () => null,
                // List items are not edited here
                getDataFieldListItems: async () => [],
                getDataFieldListItem: async () => null,
            },
        }),
        []
    );

    return (
        <Container>
            <Header5>{title}</Header5>
            <div className="inputs">
                <div className="single-col">
                    <StyledFieldLabel>{translate(Content.settings.scheduleConfig.scheduleVersionName)}</StyledFieldLabel>
                    <TextField
                        value={scheduleVersionType.name}
                        onChange={(event) => valueChange("name", event.target.value)}
                        disabled={!allowNameEdit}
                        fullWidth
                        data-testid={`name-input-${scheduleVersionType.id}`}
                    />
                </div>
                {scheduleVersionType.dataFields.length > 0 && (
                    <div className="full-width">
                        <StyledFieldLabel>{translate(Content.settings.scheduleConfig.attributes)}</StyledFieldLabel>
                        <DataFieldServicesContext.Provider value={dataFieldServices}>
                            <DataFieldsChips
                                dataFields={scheduleVersionDataFields ?? []}
                                getProjectDataFields={() => Promise.resolve([])}
                                allowEdit={false}
                                allowCreate={false}
                                AdditionalAttributeDataForm={ScheduleConfigDatafieldsAdditionalFormData}
                                showListItems={false}
                            />
                        </DataFieldServicesContext.Provider>
                    </div>
                )}
            </div>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${formFieldVerticalGap};

    .inputs {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: ${formGap};

        .single-col {
            grid-column: 1;

            ${media.extraSmall} {
                grid-column: 1 / 3;
            }
        }

        .full-width {
            grid-column: 1 / 3;
        }
    }
`;
